// src/components/PrivacyConsentField.tsx
import React, { useRef, useEffect } from "react";
import {
  Controller,
  Control,
  FieldErrors,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Check, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMembership } from "@/contexts/MembershipContext";
import { useMarket } from "@/hooks/useMarket";
import {
  marketUsesSwissConsent,
  marketRequiresAustrianConsent,
} from "@/config/market";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { FormValues } from "../types";

/**
 * Defines the translation strings required by the PrivacyConsentField component.
 * This structure supports embedding links within the consent text.
 */
export type PrivacyConsentFieldTranslations = {
  // For the Terms & Privacy Policy checkbox text
  privacyconsentfield_TosAndPp_Part1: string;
  /** CH-only ToS-alone label (AHD-11134) — its own full key set, so a copy
   *  edit to the DE/NL sentence can never ripple into the Swiss legal text. */
  privacyconsentfield_TosOnly_Part1: string;
  privacyconsentfield_TosOnly_TermsLink: string;
  privacyconsentfield_TosOnly_Part2: string;
  privacyconsentfield_TosAndPp_TermsLink: string;
  privacyconsentfield_TosAndPp_Part2: string;
  privacyconsentfield_TosAndPp_PrivacyLink: string;
  privacyconsentfield_TosAndPp_Part3: string;

  // For the Medical Data consent checkbox text
  privacyconsentfield_MedicalData_Part1: string;
  privacyconsentfield_MedicalData_ConsentLink: string;
  privacyconsentfield_MedicalData_Part2: string;
  /** CH-only medical-data consent (AHD-11937) — its own full key set, so a
   *  copy edit to the DE/NL sentence can never ripple into the Swiss legal
   *  text, and vice versa. */
  privacyconsentfield_MedicalDataCh_Part1: string;
  privacyconsentfield_MedicalDataCh_ConsentLink: string;
  privacyconsentfield_MedicalDataCh_Part2: string;

  // For the optional checkbox, a single text block is sufficient
  privacyconsentfield_Optional_Text: string;
  privacyconsentfield_Optional_Tooltip: string; // This key is now used for Popover content

  // Validation error message
  privacyconsentfield_RequiredError: string;

  // URLs for the policies, to be provided based on the current language
  privacy_policy_url: string;
  terms_conditions_url: string;
  membership_terms_url: string;
  medical_data_sharing_url: string;
};

/**
 * The short name each consent checkbox answers to.
 *
 * Every box here is labelled by its own legal sentence through
 * `aria-labelledby`, which wins over `aria-label` in the accessible-name
 * computation — so nothing below changes a single word of what a screen reader
 * announces. What it changes is whether the control can be pointed at at all:
 * a browser test records an element by its `aria-label`, and a box without one
 * falls back to its subtree text, which for these is 200 characters of
 * lawyer-approved copy that the recorder rejects as a wrapper rather than a
 * name. Nothing in the membership booking flow could be recorded past the
 * health-data box for that reason.
 */
const CONSENT_CHECKBOX_LABEL_KEYS = {
  tosAndPpConsent: "privacyConsent.ariaLabel.tosAndPp",
  medicalProcessing: "privacyConsent.ariaLabel.medicalData",
  deWunschleistungConsent: "privacyConsent.ariaLabel.deWunschleistung",
  deTreatmentConsent: "privacyConsent.ariaLabel.deTreatment",
  deConfidentialityConsent: "privacyConsent.ariaLabel.deConfidentiality",
  atLabAndDoctorConsent: "privacyConsent.ariaLabel.atLabAndDoctor",
  atConfidentialityConsent: "privacyConsent.ariaLabel.atConfidentiality",
  atWithdrawalConsent: "privacyConsent.ariaLabel.atWithdrawal",
  anonymousDataUsage: "privacyConsent.ariaLabel.anonymousDataUsage",
} satisfies Partial<Record<keyof FormValues, string>>;

/** A consent box, i.e. one that `CONSENT_CHECKBOX_LABEL_KEYS` names. Typing
 *  `renderCheckbox` with this is what makes a new box without a name a compile
 *  error instead of another unrecordable control. */
type ConsentCheckboxName = keyof typeof CONSENT_CHECKBOX_LABEL_KEYS;

interface MedicalConsentFieldProps {
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  t: PrivacyConsentFieldTranslations;
  // Used to stamp the click time of the German consents. Optional so callers
  // without the German market don't have to wire it.
  setValue?: UseFormSetValue<FormValues>;
  // Switzerland: a membership is in the cart, so the T&C label must also name
  // the Membership T&Cs (AHD-12757).
  membershipSelected?: boolean;
}

export const MedicalConsentField: React.FC<MedicalConsentFieldProps> = ({
  control,
  errors = {} as FieldErrors<FormValues>,
  t,
  setValue,
  membershipSelected = false,
}) => {
  // When the user arrives via membership mode and the membership already
  // captured T&C + medical consent at signup, hide the two required
  // checkboxes and show a short "Consented via your membership" notice.
  // The optional anonymous-data box still renders — membership doesn't
  // collect it, so we can't infer the user's preference either way.
  const { membership } = useMembership();
  const { isGermany, market } = useMarket();
  // The three German private-treatment consents are verbatim German legal text
  // (GOÄ/BGB/StGB) shown only in the German market. They live under a single
  // `germanConsent.*` key set in the `de` locale file and are always looked
  // up with `lng: "de"` — there is one German version regardless of the chosen
  // UI language (there is no separate de-DE locale).
  const { t: i18n } = useTranslation();
  const germanConsent = (key: string) => i18n(key, { lng: "de" });
  // Austria, unlike Germany, is NOT pinned to one language: the market sells in
  // German and English and both texts are maintained, so these follow the
  // interface language like ordinary copy.
  const showAustrianConsents = marketRequiresAustrianConsent(market);
  const tosAcceptedAt = membership?.tosAcceptedAt ?? null;
  const medicalConsentAcceptedAt = membership?.medicalConsentAcceptedAt ?? null;
  const hideRequiredConsents = !!tosAcceptedAt && !!medicalConsentAcceptedAt;
  const swissConsent = marketUsesSwissConsent(market);
  const chVariant: FormValues["chGtcConsentVariant"] = membershipSelected
    ? "gtc_membership"
    : "gtc_only";
  // The main correctness rule of AHD-12757: when the membership flag flips in
  // EITHER direction the label changes, and a tick given to the previous label
  // is not consent to this one. Untick and clear the record, so submit is
  // blocked until the user has seen and accepted the label now on screen.
  // Skipped on mount (a restored tick under an unchanged label stays valid).
  const prevMembershipSelected = useRef(membershipSelected);
  useEffect(() => {
    if (prevMembershipSelected.current === membershipSelected) return;
    prevMembershipSelected.current = membershipSelected;
    if (!swissConsent || !setValue) return;
    setValue("tosAndPpConsent", false);
    setValue("chGtcConsentAt", undefined);
    setValue("chGtcConsentVariant", undefined);
    setValue("chGtcConsentLabel", undefined);
  }, [membershipSelected, swissConsent, setValue]);
  // Watch the values of both required checkboxes to control the state of the optional one
  const tosAndPpConsentValue = useWatch({ control, name: "tosAndPpConsent" });
  const medicalProcessingValue = useWatch({
    control,
    name: "medicalProcessing",
  });

  // Ref to hold the `onChange` function for the optional checkbox
  // This allows the required checkboxes to update it directly (cascading uncheck)
  const anonymousOnChangeRef = useRef<(v: boolean) => void>(() => {});

  // Helper function to create checkbox component
  const renderCheckbox = (
    name: ConsentCheckboxName,
    isRequired: boolean,
    contentRenderer: (toggleFn: () => void) => React.ReactNode,
    error?: FieldErrors<FormValues>[keyof FormValues],
    disabled?: boolean,
    cascadeUncheck?: boolean,
    timestampField?: keyof FormValues,
  ) => {
    return (
      <Controller
        name={name}
        control={control}
        rules={{ required: isRequired }}
        render={({ field }) => {
          // Helper to toggle state and handle cascading uncheck
          const toggleConsent = (newValue?: boolean) => {
            const valueToSet = newValue !== undefined ? newValue : !field.value;
            field.onChange(valueToSet);
            // Record (or clear) the click time of this consent.
            if (timestampField && setValue) {
              setValue(
                timestampField,
                valueToSet ? new Date().toISOString() : undefined,
              );
            }
            // Switzerland: the record is the label the user actually saw, read
            // back from the DOM at the tick (not re-derived at submit), plus
            // the variant that produced it (AHD-12757).
            if (name === "tosAndPpConsent" && swissConsent && setValue) {
              setValue(
                "chGtcConsentVariant",
                valueToSet ? chVariant : undefined,
              );
              setValue(
                "chGtcConsentLabel",
                valueToSet
                  ? document
                      .getElementById("tosAndPpConsent-label")
                      ?.textContent?.replace(/\s+/g, " ")
                      .trim()
                  : undefined,
              );
            }
            // If we are unchecking a required consent and cascadeUncheck is true, also uncheck the optional one
            if (!valueToSet && cascadeUncheck) {
              anonymousOnChangeRef.current(false);
            }
          };

          return (
            <div className="space-y-2">
              <div
                className={cn(
                  "flex items-start space-x-3 transition-opacity",
                  disabled && "opacity-50",
                )}
              >
                <div className="flex-shrink-0">
                  <div
                    onClick={() => {
                      if (disabled) return;
                      toggleConsent();
                    }}
                    className={cn(
                      "w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200",
                      disabled
                        ? "border-gray-300 bg-gray-100 cursor-not-allowed"
                        : "cursor-pointer",
                      field.value
                        ? "border-transparent"
                        : "border-gray-400 hover:border-gray-500",
                      error && "border-red-500",
                    )}
                    style={{
                      background:
                        field.value && !disabled
                          ? "linear-gradient(135deg, #4C2652 0%, #5C4279 100%)"
                          : "transparent",
                    }}
                    role="checkbox"
                    aria-checked={!!field.value}
                    aria-label={i18n(CONSENT_CHECKBOX_LABEL_KEYS[name])}
                    aria-labelledby={`${name}-label`}
                    aria-describedby={error ? `${name}-error` : undefined}
                    aria-disabled={disabled}
                    tabIndex={disabled ? -1 : 0}
                    onKeyDown={(e) => {
                      if (disabled) return;
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        toggleConsent();
                      }
                    }}
                  >
                    {field.value && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>

                <div className="flex-1 text-xs leading-relaxed">
                  {contentRenderer(() => {
                    if (disabled) return;
                    toggleConsent();
                  })}
                </div>
              </div>

              {error && (
                <span
                  id={`${name}-error`}
                  className="text-sm text-red-600 ml-8 block"
                >
                  {t.privacyconsentfield_RequiredError}
                </span>
              )}
            </div>
          );
        }}
      />
    );
  };

  return (
    <div className="font-sans text-gray-800">
      <div className="space-y-4">
        {hideRequiredConsents ? null : (
          <>
            {/* ===== Terms & Privacy Policy Checkbox (tosAndPpConsent) ===== */}
            {renderCheckbox(
              "tosAndPpConsent",
              true,
              (toggleFn) => (
                <label
                  id="tosAndPpConsent-label"
                  onClick={toggleFn}
                  className="text-xs text-gray-700 cursor-pointer leading-relaxed"
                >
                  {/* CH consents to the ToS alone (AHD-11134): per Kellerhals
                      Carrard the Privacy Policy is not a contract, so asking for
                      read-confirmation could disadvantage us later. DE keeps the
                      full sentence — its wording is the GNP-approved consent
                      set — and NL keeps the original copy. The CH variant has
                      its OWN complete key set (privacyConsent.tosOnly.*): no
                      string is shared with tosAndPp, so a copy edit to the
                      DE/NL sentence can never ripple into the Swiss legal
                      text. */}
                  {swissConsent && membershipSelected ? (
                    /* Membership in the cart (AHD-12757): the Membership T&Cs
                       are not incorporated by the general T&Cs, so they must
                       be named and linked in the same sentence. ONE key holding
                       the whole sentence, with the two links as tag slots, so
                       a translator can reorder the clause; the general T&C
                       sentences above and below predate this and still ship
                       as part1/link/part2 fragments. */
                    <Trans
                      t={i18n}
                      i18nKey="privacyConsent.tosMembership"
                      components={{
                        terms: (
                          <a
                            href={t.terms_conditions_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium underline text-[rgb(92,66,121)]"
                            onClick={(e) => e.stopPropagation()} // Prevent checkbox toggle when clicking the link
                            tabIndex={-1}
                          />
                        ),
                        membershipTerms: (
                          <a
                            href={t.membership_terms_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium underline text-[rgb(92,66,121)]"
                            onClick={(e) => e.stopPropagation()} // Prevent checkbox toggle when clicking the link
                            tabIndex={-1}
                          />
                        ),
                      }}
                    />
                  ) : swissConsent ? (
                    <>
                      {t.privacyconsentfield_TosOnly_Part1}
                      <a
                        href={t.terms_conditions_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium underline text-[rgb(92,66,121)]"
                        onClick={(e) => e.stopPropagation()} // Prevent checkbox toggle when clicking the link
                        tabIndex={-1}
                      >
                        {t.privacyconsentfield_TosOnly_TermsLink}
                      </a>
                      {t.privacyconsentfield_TosOnly_Part2}
                    </>
                  ) : (
                    <>
                      {t.privacyconsentfield_TosAndPp_Part1}
                      <a
                        href={t.terms_conditions_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium underline text-[rgb(92,66,121)]"
                        onClick={(e) => e.stopPropagation()} // Prevent checkbox toggle when clicking the link
                        tabIndex={-1}
                      >
                        {t.privacyconsentfield_TosAndPp_TermsLink}
                      </a>
                      {t.privacyconsentfield_TosAndPp_Part2}
                      <a
                        href={t.privacy_policy_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium underline text-[rgb(92,66,121)]"
                        onClick={(e) => e.stopPropagation()} // Prevent checkbox toggle
                        tabIndex={-1}
                      >
                        {t.privacyconsentfield_TosAndPp_PrivacyLink}
                      </a>
                      {t.privacyconsentfield_TosAndPp_Part3}
                    </>
                  )}
                </label>
              ),
              errors.tosAndPpConsent,
              false,
              true,
              swissConsent ? "chGtcConsentAt" : undefined,
            )}

            {/* ===== Medical Data Consent Checkbox (medicalProcessing) ===== */}
            {renderCheckbox(
              "medicalProcessing",
              true,
              (toggleFn) => (
                <label
                  id="medicalProcessing-label"
                  onClick={toggleFn}
                  className="text-xs text-gray-700 cursor-pointer leading-relaxed"
                >
                  {/* CH gets lawyer-approved wording that names WHAT is shared
                      and WITH WHOM (AHD-11937), under its own complete key set
                      (privacyConsent.medicalDataCh.*) — DE and NL have
                      different legal advice and keep medicalData.*, so neither
                      side's copy can ripple into the other. Both variants are
                      part1 + link + part2 because the link sits mid-sentence
                      in German and at the end in English and French. */}
                  {marketUsesSwissConsent(market) ? (
                    <>
                      {t.privacyconsentfield_MedicalDataCh_Part1}
                      <a
                        href={t.medical_data_sharing_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium underline text-[rgb(92,66,121)]"
                        onClick={(e) => e.stopPropagation()} // Prevent checkbox toggle when clicking the link
                        tabIndex={-1}
                      >
                        {t.privacyconsentfield_MedicalDataCh_ConsentLink}
                      </a>
                      {t.privacyconsentfield_MedicalDataCh_Part2}
                    </>
                  ) : (
                    <>
                      {t.privacyconsentfield_MedicalData_Part1}
                      <a
                        href={t.medical_data_sharing_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium underline text-[rgb(92,66,121)]"
                        onClick={(e) => e.stopPropagation()} // Prevent checkbox toggle when clicking the link
                        tabIndex={-1}
                      >
                        {t.privacyconsentfield_MedicalData_ConsentLink}
                      </a>
                      {t.privacyconsentfield_MedicalData_Part2}
                    </>
                  )}
                </label>
              ),
              errors.medicalProcessing,
              false,
              true,
            )}
          </>
        )}

        {/* ===== Germany-only private-treatment consents ===== */}
        {isGermany && (
          <>
            {renderCheckbox(
              "deWunschleistungConsent",
              true,
              (toggleFn) => (
                <label
                  id="deWunschleistungConsent-label"
                  onClick={toggleFn}
                  className="text-xs text-gray-700 cursor-pointer leading-relaxed"
                >
                  {germanConsent("germanConsent.wunschleistung")}
                </label>
              ),
              errors.deWunschleistungConsent,
              false,
              false,
              "deWunschleistungConsentAt",
            )}

            {renderCheckbox(
              "deTreatmentConsent",
              true,
              (toggleFn) => (
                <label
                  id="deTreatmentConsent-label"
                  onClick={toggleFn}
                  className="text-xs text-gray-700 cursor-pointer leading-relaxed"
                >
                  {germanConsent("germanConsent.treatment")}
                </label>
              ),
              errors.deTreatmentConsent,
              false,
              false,
              "deTreatmentConsentAt",
            )}

            {renderCheckbox(
              "deConfidentialityConsent",
              true,
              (toggleFn) => (
                <label
                  id="deConfidentialityConsent-label"
                  onClick={toggleFn}
                  className="text-xs text-gray-700 cursor-pointer leading-relaxed"
                >
                  {germanConsent("germanConsent.confidentiality")}
                </label>
              ),
              errors.deConfidentialityConsent,
              false,
              false,
              "deConfidentialityConsentAt",
            )}
          </>
        )}

        {/* ===== Austria-only consents =====
            Verbatim Austrian legal text, required, and separate boxes on
            purpose: each one is a distinct legal act (two contracts, a
            confidentiality release under § 54 ÄrzteG 1998, and the express
            request to start before the withdrawal period expires), so they
            have to be agreed to individually rather than bundled into one
            tick. The optional anonymised-data box below is the fourth item of
            the Austrian set and already renders for every non-CH market, so it
            is not repeated here. */}
        {showAustrianConsents && (
          <>
            {renderCheckbox(
              "atLabAndDoctorConsent",
              true,
              (toggleFn) => (
                <label
                  id="atLabAndDoctorConsent-label"
                  onClick={toggleFn}
                  className="text-xs text-gray-700 cursor-pointer leading-relaxed"
                >
                  {i18n("austrianConsent.labAndDoctorContract")}
                </label>
              ),
              errors.atLabAndDoctorConsent,
              false,
              false,
              "atLabAndDoctorConsentAt",
            )}

            {renderCheckbox(
              "atConfidentialityConsent",
              true,
              (toggleFn) => (
                <label
                  id="atConfidentialityConsent-label"
                  onClick={toggleFn}
                  className="text-xs text-gray-700 cursor-pointer leading-relaxed"
                >
                  {i18n("austrianConsent.confidentialityRelease")}
                </label>
              ),
              errors.atConfidentialityConsent,
              false,
              false,
              "atConfidentialityConsentAt",
            )}

            {renderCheckbox(
              "atWithdrawalConsent",
              true,
              (toggleFn) => (
                <label
                  id="atWithdrawalConsent-label"
                  onClick={toggleFn}
                  className="text-xs text-gray-700 cursor-pointer leading-relaxed"
                >
                  {i18n("austrianConsent.withdrawalWaiver")}
                </label>
              ),
              errors.atWithdrawalConsent,
              false,
              false,
              "atWithdrawalConsentAt",
            )}
          </>
        )}

        {/* ===== Optional Checkbox (anonymousDataUsage) ===== */}
        {/* Hidden for CH (AHD-11130): Kellerhals Carrard confirmed truly
            anonymised data needs no consent under the nFADP, so the box asked
            for something the law does not require. The form field keeps its
            `false` default, so `anonymous_data_usage_consent_date` stays NULL —
            "remove the checkbox, leave data empty". DE keeps it: the optional
            box is part of the GNP-approved German checkbox set. */}
        {market !== "ch" && (
          <Controller
            name="anonymousDataUsage"
            control={control}
            render={({ field }) => {
              // Store the latest onChange function in the ref
              anonymousOnChangeRef.current = field.onChange;

              // In membership mode the required consents come from the
              // membership record, so the optional box is always enabled.
              const disabled =
                !hideRequiredConsents &&
                (!tosAndPpConsentValue || !medicalProcessingValue);

              return (
                <div
                  className={cn(
                    "space-y-2 transition-opacity",
                    disabled && "opacity-50",
                  )}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <div
                        onClick={() => {
                          if (disabled) return;
                          field.onChange(!field.value);
                        }}
                        className={cn(
                          "w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200",
                          disabled
                            ? "border-gray-300 bg-gray-100 cursor-not-allowed"
                            : "cursor-pointer",
                          field.value
                            ? "border-transparent"
                            : "border-gray-400 hover:border-gray-500",
                        )}
                        style={{
                          background:
                            field.value && !disabled
                              ? "linear-gradient(135deg, #4C2652 0%, #5C4279 100%)"
                              : "transparent",
                        }}
                        role="checkbox"
                        aria-checked={!!field.value}
                        aria-label={i18n(
                          CONSENT_CHECKBOX_LABEL_KEYS.anonymousDataUsage,
                        )}
                        aria-labelledby="anonymousDataUsage-label"
                        aria-disabled={disabled}
                        tabIndex={disabled ? -1 : 0}
                        onKeyDown={(e) => {
                          if (disabled) return;
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            field.onChange(!field.value);
                          }
                        }}
                      >
                        {field.value && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                    </div>

                    <div className="flex-1">
                      <label
                        id="anonymousDataUsage-label"
                        onClick={() => {
                          if (disabled) return;
                          field.onChange(!field.value);
                        }}
                        className={cn(
                          "text-xs text-gray-700 leading-relaxed",
                          "flex items-center space-x-2",
                          disabled ? "cursor-not-allowed" : "cursor-pointer",
                        )}
                      >
                        <span className="mt-[1px]">
                          {t.privacyconsentfield_Optional_Text}
                        </span>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Info
                              className={cn(
                                "w-4 h-4 text-gray-400 cursor-pointer flex-shrink-0 mt-0.5",
                                disabled && "opacity-50",
                              )}
                              // Prevent the click from bubbling to the label and toggling the checkbox
                              onClick={(e) => {
                                if (!disabled) {
                                  e.stopPropagation();
                                }
                              }}
                            />
                          </PopoverTrigger>
                          <PopoverContent className="w-80">
                            <p className="text-sm">
                              {t.privacyconsentfield_Optional_Tooltip}
                            </p>
                          </PopoverContent>
                        </Popover>
                      </label>
                    </div>
                  </div>
                </div>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};
